import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

/**
 * Shown only on the 29th of any month, in Pacific time, until 2:00 PM PT.
 * Reminds users that backdating closes at 2:00 PM Pacific and the leaderboard
 * is finalized at that moment.
 */
const PT = 'America/Vancouver';
const CUTOFF_HOUR = 14;

function getPTParts() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PT,
    year: 'numeric', month: 'long', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return {
    day: parseInt(get('day'), 10),
    monthName: get('month'),
    hour: parseInt(get('hour'), 10),
    minute: parseInt(get('minute'), 10),
    second: parseInt(get('second'), 10),
  };
}

function getRemainingSeconds() {
  const { day, hour, minute, second } = getPTParts();
  if (day !== 29 || hour >= CUTOFF_HOUR) return null;
  return (CUTOFF_HOUR - hour) * 3600 - minute * 60 - second;
}

function format(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const MonthEndCountdownBanner = () => {
  const [remaining, setRemaining] = useState(() => getRemainingSeconds());
  const monthName = getPTParts().monthName;

  useEffect(() => {
    const id = setInterval(() => setRemaining(getRemainingSeconds()), 30000);
    return () => clearInterval(id);
  }, []);

  if (remaining === null) return null;

  return (
    <div
      className="mb-3 sm:mb-4 md:mb-6 bg-[#EFB342]/10 border border-[#EFB342]/50 rounded-xl px-4 py-3 flex items-center gap-3 animate-fade-in"
      data-testid="month-end-countdown-banner"
    >
      <Clock className="text-[#B8871E] flex-shrink-0" size={20} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-stone-900">
          Final hours to log {monthName}
        </p>
        <p className="text-xs text-stone-700 mt-0.5">
          Backdating closes at <span className="text-[#B8871E] font-semibold">2:00 PM Pacific</span>
          {' · '}
          <span data-testid="month-end-countdown-remaining">{format(remaining)} remaining</span>
        </p>
      </div>
    </div>
  );
};

export default MonthEndCountdownBanner;
